import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Grid} from 'src/design/atoms/Grid';
import {Stack} from 'src/design/atoms/Stack';
import React from 'react';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Spinner} from 'src/design/atoms/Spinner';
import {ContentTitle} from 'src/design/atoms/Typography';
import {ViewField} from 'src/design/atoms/ViewField';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {useGetOshaSummaryQuery} from 'src/redux/queries/osha';

export interface OshaSummaryCardProps {
  projectId: number;
  year: number;
}

export const OshaSummaryCard: React.FC<OshaSummaryCardProps> = ({
  projectId,
  year,
}) => {
  const {data: summary, isLoading, isError} = useGetOshaSummaryQuery({
    projectId,
    year,
  });

  if (isLoading) {
    return (
      <SectionCard title="OSHA 300A Summary">
        <Box p="md">
          <Spinner loadingText="Loading summary..." />
        </Box>
      </SectionCard>
    );
  }

  if (isError || !summary) {
    return (
      <SectionCard title="OSHA 300A Summary">
        <Box p="md">
          <ErrorAlert message="The summary could not be loaded. Please try again." />
        </Box>
      </SectionCard>
    );
  }

  const cases = [
    {label: 'Total number of deaths (G)', value: summary.totalDeaths},
    {
      label: 'Total number of cases with days away from work (H)',
      value: summary.totalCasesWithDaysAway,
    },
    {
      label: 'Total number of cases with job transfer or restriction (I)',
      value: summary.totalCasesWithRestriction,
    },
    {
      label: 'Total number of other recordable cases (J)',
      value: summary.totalOtherRecordableCases,
    },
  ];

  const days = [
    {label: 'Total number of days away from work (K)', value: summary.totalDaysAway},
    {
      label: 'Total number of days of job transfer or restriction (L)',
      value: summary.totalRestrictionDays,
    },
  ];

  const illnesses = [
    {label: '(1) Injury', value: summary.totalInjuries},
    {label: '(2) Skin Disorder', value: summary.totalSkinDisorders},
    {label: '(3) Respiratory Condition', value: summary.totalRespiratoryConditions},
    {label: '(4) Poisoning', value: summary.totalPoisonings},
    {label: '(5) Hearing Loss', value: summary.totalHearingLoss},
    {label: '(6) All Other Illnesses', value: summary.totalAllOtherIllnesses},
  ];

  return (
    <SectionCard title="OSHA 300A Summary">
      <Box p="md">
        <Stack gap="lg">
          <Box>
            <ContentTitle text="Number of Cases" mb="sm" />
            <Grid gutter="md">
              {cases.map(item => (
                <Grid.Col key={item.label} span={{base: 12, sm: 6}}>
                  <ViewField label={item.label} value={item.value.toLocaleString()} />
                </Grid.Col>
              ))}
            </Grid>
          </Box>

          <Divider />

          <Box>
            <ContentTitle text="Number of Days" mb="sm" />
            <Grid gutter="md">
              {days.map(item => (
                <Grid.Col key={item.label} span={{base: 12, sm: 6}}>
                  <ViewField label={item.label} value={item.value.toLocaleString()} />
                </Grid.Col>
              ))}
            </Grid>
          </Box>

          <Divider />

          <Box>
            <ContentTitle text="Injury and Illness Types" mb="sm" />
            <Grid gutter="md">
              {illnesses.map(item => (
                <Grid.Col key={item.label} span={{base: 12, sm: 6}}>
                  <ViewField label={item.label} value={item.value.toLocaleString()} />
                </Grid.Col>
              ))}
            </Grid>
          </Box>
        </Stack>
      </Box>
    </SectionCard>
  );
};
